import React, { useCallback, useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";
import type { StylesConfig } from "react-select";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { format } from "date-fns";

import { AppContext } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import type { TemplateOption } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import type { HubspotPanelNode } from "~/stream/SideBar/CRMPanel/Hubspot";
import { type HubspotOptionType, HubspotSelect } from "~/stream/SideBar/CRMPanel/Hubspot/HubspotSelect";
import { Button, Checkbox } from "~/ui";
import { Link, Modal } from "~/ui/legacyFellowIndex";
import Selector, { type SingleValue } from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import { getDateStringForSendNoteModal, getRecapTemplateOptions, recapSelectorStyles } from "../utils";
import { useHubspotTasksIntegrationFeatureFlagOn } from "./featureFlag";
import type { ModalProviderProps } from "./HubspotPanel";

import type { HubspotSendNotesModalQuery } from "~/__generated__/HubspotSendNotesModalQuery.graphql";
import type { HubspotSendNotesMutation as HubspotSendNotesMutationType } from "~/__generated__/HubspotSendNotesMutation.graphql";

import styles from "./css/HubspotSendNotes.module.css";

const HUBSPOT_AUTOMATION_PRO_TIP_DISMISS_KEY = "hubspot-automation-pro-tip-dismiss-key";

const hubspotSendNotesModalQuery = graphql`
	query HubspotSendNotesModalQuery($noteId: ID!) {
		note: node(id: $noteId) {
			... on Note {
				title
				eventStart
				eventEnd
				event {
					isAllDay
				}
				meetingRecordings {
					id
					activeSummary {
						id
						templatedRecaps
						activeRecapTemplate
					}
				}
			}
		}
	}
`;

const sendNoteAsCallMutation = graphql`
	mutation HubspotSendNotesMutation(
		$noteId: String!
		$associations: Associations
		$includeRecap: Boolean!
		$sendActionItems: Boolean!
		$recapTemplateType: RecapTemplateType
	) {
		sendNoteToHubspotAsCall(
			input: {
				noteId: $noteId
				associations: $associations
				includeRecap: $includeRecap
				sendActionItems: $sendActionItems
				recapTemplateType: $recapTemplateType
			}
		) {
			ok
			error
			id
			url
			noteVersion
		}
	}
`;

type HubspotSendNotesModalProps = ModalProviderProps<HubspotPanelNode>;

function contactItemToNode<a extends string>(
	a: a,
	option: Omit<Extract<HubspotOptionType, { __typename: a }>, "__typename">,
) {
	return {
		...option,
		__typename: a,
	};
}

function panelNodesToOptions(panelNode: HubspotPanelNode): HubspotOptionType[] {
	const company =
		"company" in panelNode && panelNode.company ? contactItemToNode("HubspotCompanyNode", panelNode.company) : false;
	const deals = "deals" in panelNode ? panelNode.deals.map(c => contactItemToNode("HubspotDealNode", c)) : [];

	return [...(panelNode ? [panelNode] : []), ...(company ? [company] : []), ...deals];
}

export const HubspotSendNotesModal: React.FunctionComponent<HubspotSendNotesModalProps> = props => {
	const { modalMode, onClose, defaultOption, noteId, sendNotesAutomationIsActive, shouldShowAutomationInfoBox } = props;
	const data = useLazyLoadQuery<HubspotSendNotesModalQuery>(hubspotSendNotesModalQuery, { noteId });
	const defaultModalOptions = defaultOption ? panelNodesToOptions(defaultOption) : [];
	const [selectedOptions, setSelectedOptions] = useState<readonly HubspotOptionType[]>(() => defaultModalOptions);
	const [includeRecap, setIncludeRecap] = useState(true);
	const [sendActionItems, setSendActionItems] = useState(true);
	const [automationProTipDismissed, setAutomationProTipDismissed] = useState(
		store.has(HUBSPOT_AUTOMATION_PRO_TIP_DISMISS_KEY),
	);

	const shouldDisableIncludeRecapCheckbox = data.note?.meetingRecordings?.length === 0;
	const isHubspotTasksIntegrationFeatureOn = useHubspotTasksIntegrationFeatureFlagOn();

	const recapTemplateOptions = getRecapTemplateOptions(
		data.note?.meetingRecordings?.[0]?.activeSummary?.templatedRecaps ?? {},
	);

	const shouldShowRecapTemplateOptions = data.note?.meetingRecordings?.length === 1 && recapTemplateOptions.length > 1;

	const [selectedRecapTemplate, setSelectedRecapTemplate] = React.useState<SingleValue<TemplateOption>>(
		recapTemplateOptions.find(o => o.value === data.note?.meetingRecordings?.[0]?.activeSummary?.activeRecapTemplate) ??
			recapTemplateOptions[0],
	);

	const { openSettingsModal } = React.useContext(AppContext);
	const openIntegrationSettings = React.useCallback(() => {
		openSettingsModal(["apps-and-integrations", "hubspot"]);
	}, [openSettingsModal]);

	const dismissAutomationProTip = useCallback(() => {
		store.set(HUBSPOT_AUTOMATION_PRO_TIP_DISMISS_KEY, true);
		setAutomationProTipDismissed(true);
	}, []);

	const onSendNote = useCallback(() => {
		const associations: { companyIds: string[]; contactIds: string[]; dealIds: string[] } = {
			companyIds: [],
			contactIds: [],
			dealIds: [],
		};

		selectedOptions.forEach(option => {
			switch (option.__typename) {
				case "HubspotContactPanelNode":
				case "HubspotContactNode":
					associations.contactIds.push(option.id);
					break;
				case "HubspotCompanyNode":
					associations.companyIds.push(option.id);
					break;
				case "HubspotDealNode":
					associations.dealIds.push(option.id);
					break;
			}
		});

		const recapTemplateType = selectedRecapTemplate?.value === "GENERAL" ? null : selectedRecapTemplate?.value;

		commitMutation<HubspotSendNotesMutationType>(environment, {
			mutation: sendNoteAsCallMutation,
			variables: {
				noteId,
				associations: associations,
				includeRecap,
				sendActionItems: isHubspotTasksIntegrationFeatureOn ? sendActionItems : false,
				recapTemplateType,
			},
			onCompleted: data => {
				const url = data.sendNoteToHubspotAsCall?.url;

				if (url) {
					dispatchAnalyticsTrack("Hubspot Note Sent", {
						associations: selectedOptions.map(option => option.__typename),
						noteId,
						url,
						id: data.sendNoteToHubspotAsCall?.id,
					});

					dispatchAnalyticsTrack("Note sent to HubSpot", {
						noteIncluded: !!data.sendNoteToHubspotAsCall.noteVersion,
						recapIncluded: includeRecap,
						autoSent: false,
						sendUxLocation: modalMode ? "Modal" : "Panel",
					});

					toast.success("Note sent to HubSpot successfully", {
						actions: (
							// TODO: Replace with some sort of shared button component that is a react-router link
							<Link className={styles.button} href={url} target="_blank" rel="noopener noreferrer">
								View in HubSpot
							</Link>
						),
						closeOnClick: true,
						closeButton: true,
						draggable: false,
						pauseOnHover: true,
					});
				} else {
					toast.error("We were not able send this call to HubSpot.");
				}
			},
		});
		onClose();
	}, [
		includeRecap,
		sendActionItems,
		isHubspotTasksIntegrationFeatureOn,
		modalMode,
		noteId,
		onClose,
		selectedOptions,
		selectedRecapTemplate?.value,
	]);

	const onChangeIncludeRecap = useCallback(() => {
		setIncludeRecap(!includeRecap);
	}, [includeRecap]);

	const onChangeSendActionItems = useCallback(() => setSendActionItems(prev => !prev), []);

	const onChangeRecapTemplate = useCallback((option: SingleValue<TemplateOption>) => {
		setSelectedRecapTemplate(option);
	}, []);

	const content = (
		<>
			<Modal.Body className={styles.modalContent}>
				<div className={styles.selector}>
					<div className={styles.selectorLabel}>Select Contact(s), Company, or Deal(s)</div>
					<HubspotSelect setSelectedOptions={setSelectedOptions} defaultOptions={defaultModalOptions} />
				</div>
				<div>
					<div className={cx(styles.includeRecapSetting, { [styles.disabled]: shouldDisableIncludeRecapCheckbox })}>
						<Checkbox
							id="include-recap-checkbox"
							checked={shouldDisableIncludeRecapCheckbox ? false : includeRecap}
							onCheckedChange={onChangeIncludeRecap}
							disabled={shouldDisableIncludeRecapCheckbox}
						/>
						<label htmlFor="include-recap-checkbox">Include AI recap and link to recording</label>
					</div>
					{isHubspotTasksIntegrationFeatureOn && (
						<div className={styles.includeRecapSetting}>
							<Checkbox
								id="send-action-items-checkbox"
								checked={sendActionItems}
								onCheckedChange={onChangeSendActionItems}
							/>
							<label htmlFor="send-action-items-checkbox">Create tasks from action items</label>
						</div>
					)}
					{shouldShowRecapTemplateOptions && (
						<>
							<label
								className={cx(styles.recapTemplateLabel, {
									[styles.disabled]: shouldDisableIncludeRecapCheckbox || !includeRecap,
								})}
							>
								Recap Template
							</label>
							<Selector<TemplateOption>
								styles={recapSelectorStyles as StylesConfig<TemplateOption, boolean>}
								options={recapTemplateOptions}
								value={selectedRecapTemplate}
								onChange={onChangeRecapTemplate}
								isDisabled={!includeRecap}
							/>
						</>
					)}
				</div>
				{shouldShowAutomationInfoBox &&
					(sendNotesAutomationIsActive ? (
						<div className={styles.automationInfoBox}>
							<div className={styles.automationActiveInfoBox}>
								<FontAwesomeIcon icon={faInfoCircle} className={styles.infoCircleIcon} />
								<span>
									This note will be automatically sent 3hrs after the meeting. Click &rsquo;Send&rsquo; to also send it
									immediately.
								</span>
							</div>
						</div>
					) : (
						!automationProTipDismissed && (
							<div className={styles.automationInfoBox}>
								Pro tip: You can now automatically send your note and recap to HubSpot after all meetings
								<div className={styles.automationInfoBoxButtons}>
									<Button size="slim" variant="primary" onClick={openIntegrationSettings}>
										Configure
									</Button>
									<Button size="slim" variant="secondary" onClick={dismissAutomationProTip}>
										Dismiss
									</Button>
								</div>
							</div>
						)
					))}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={onSendNote} disabled={selectedOptions.length < 1}>
					Send
				</Button>
			</Modal.Footer>
		</>
	);

	if (!modalMode) {
		return content;
	}

	const dateString = getDateStringForSendNoteModal(new Date(data.note?.eventStart || ""));
	const timeString = data.note?.eventStart ? format(new Date(data.note?.eventStart), "h:mm a") : "";

	return (
		<Modal onClose={props.onClose}>
			<Modal.Header onClose={props.onClose}>
				<Modal.Title>
					Send note to HubSpot
					<div className={styles.modalSubtitle}>
						<strong>{data.note?.title}</strong>
						{dateString && timeString && ` ${dateString} at ${timeString}`}
					</div>
				</Modal.Title>
			</Modal.Header>
			{content}
		</Modal>
	);
};

export default HubspotSendNotesModal;
